import { z } from "zod";

import { ApiSuccessSchema } from "../../common/response.schema";
import {
  AnalyticsReportScheduleSchema,
  CreateAnalyticsReportScheduleBodySchema,
  DeleteAnalyticsReportScheduleResponseSchema,
  ListAnalyticsReportRunsSchema,
  ListAnalyticsReportSchedulesSchema,
  ListAnalyticsReportSlackChannelsSchema,
  RunAnalyticsReportNowResponseSchema,
  UpdateAnalyticsReportScheduleBodySchema
} from "../../public/v1/schemas/analytics-report.schemas";
import { UuidParamSchema } from "../../public/v1/schemas/common.schemas";
import type { InferSchemas } from "../../schema-inference";

/**
 * Analytics scheduled reports — internal (session-authed) API contracts.
 *
 * The same CRUD + run-history + run-now surface the public v1 API exposes, but
 * reached with session/JWT auth so the in-app Analytics dashboard can manage
 * schedules. The org comes from the authenticated session
 * (`@CurrentOrganizationId`), never an api key. Schemas are reused verbatim from
 * the public v1 layer — one contract, two auth front doors, mirroring
 * {@link ZAnalytics}.
 */
export const ZAnalyticsReports = {
  /** GET /api/analytics/reports — list this org's report schedules. */
  List: {
    method: "GET" as const,
    path: "/api/analytics/reports",
    Response: ApiSuccessSchema(ListAnalyticsReportSchedulesSchema)
  },

  /** GET /api/analytics/reports/:id — one report schedule. */
  Get: {
    method: "GET" as const,
    path: "/api/analytics/reports/:id",
    PathVars: UuidParamSchema,
    Response: ApiSuccessSchema(AnalyticsReportScheduleSchema)
  },

  /** GET /api/analytics/reports/:id/runs — a schedule's run history. */
  Runs: {
    method: "GET" as const,
    path: "/api/analytics/reports/:id/runs",
    PathVars: UuidParamSchema,
    Response: ApiSuccessSchema(ListAnalyticsReportRunsSchema)
  },

  /**
   * GET /api/analytics/reports/slack-channels/:deploymentId — the channels a
   * Slack deployment's bot can see, for the delivery-target channel picker.
   */
  ListSlackChannels: {
    method: "GET" as const,
    path: "/api/analytics/reports/slack-channels/:deploymentId",
    PathVars: z.object({ deploymentId: z.string().uuid() }),
    Response: ApiSuccessSchema(ListAnalyticsReportSlackChannelsSchema)
  },

  /** POST /api/analytics/reports — create a scheduled report. */
  Create: {
    method: "POST" as const,
    path: "/api/analytics/reports",
    Body: CreateAnalyticsReportScheduleBodySchema,
    Response: ApiSuccessSchema(AnalyticsReportScheduleSchema)
  },

  /** POST /api/analytics/reports/:id/run — fire the schedule's tick now. */
  RunNow: {
    method: "POST" as const,
    path: "/api/analytics/reports/:id/run",
    PathVars: UuidParamSchema,
    Response: ApiSuccessSchema(RunAnalyticsReportNowResponseSchema)
  },

  /** PATCH /api/analytics/reports/:id — update a schedule (also pause/resume). */
  Update: {
    method: "PATCH" as const,
    path: "/api/analytics/reports/:id",
    PathVars: UuidParamSchema,
    Body: UpdateAnalyticsReportScheduleBodySchema,
    Response: ApiSuccessSchema(AnalyticsReportScheduleSchema)
  },

  /** DELETE /api/analytics/reports/:id — delete a schedule. */
  Delete: {
    method: "DELETE" as const,
    path: "/api/analytics/reports/:id",
    PathVars: UuidParamSchema,
    Response: ApiSuccessSchema(DeleteAnalyticsReportScheduleResponseSchema)
  }
} as const;

export type TApiAnalyticsReports = InferSchemas<typeof ZAnalyticsReports>;
